// NewsProxyAPIManager.js
// Neutral manager that calls your internal proxy (can aggregate any provider).
class NewsProxyAPIManager {
    constructor() {
        this.isInitialized = false;
        this.baseUrl = 'https://api.palosverdestech.com/news/';
    }

    async initialize() {
        // No API keys or config needed for the proxy
        this.isInitialized = true;
    }

    static async create() {
        if (!NewsProxyAPIManager.instance) {
            const instance = new NewsProxyAPIManager();
            await instance.initialize();
            NewsProxyAPIManager.instance = instance;
        }
        return NewsProxyAPIManager.instance;
    }

    /**
     * Fetch news for a symbol. Prefer "EXCHANGE:TICKER" (e.g., "NYSE:IBM").
     * Extra params kept for compatibility with existing callers but unused.
     */
    async fetchStockNews(stockSymbol, _limit = 10, _startDate = null, _endDate = null) {
        try {
            if (!this.isInitialized) throw new Error('Proxy client not initialized.');

            // Proxy expects a plain path with a literal colon: /news/NYSE:IBM
            const url = this.baseUrl + stockSymbol;
            if (__LOG_INFO__) AppLog.info(`📡 Proxy fetch: ${url}`);

            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const json = await response.json();

            // Accept several shapes: [], {articles:[]}, {results:[]}, {data:[]}
            const rows = Array.isArray(json) ? json
                : Array.isArray(json.articles) ? json.articles
                    : Array.isArray(json.results) ? json.results
                        : Array.isArray(json.data) ? json.data
                            : [];

            // Normalize fields expected by UI with HTML entity decoding
            const articles = rows.map(a => ({
                title: he.decode(a.title || a.headline || 'No title available'),
                publisher: NewsProxyAPIManager.cleanPublisher(a.source, a.url || a.article_url, a.publisher || a.domain),
                publishedDate: a.published_at || a.publishedAt || a.date || a.published || new Date().toISOString(),
                url: a.url || a.article_url || '#',
                description: he.decode(a.description || a.summary || ''),
                tickers: Array.isArray(a.tickers) && a.tickers.length ? a.tickers : [stockSymbol]
            }));

            const limitedArticles = articles.slice(0, 7);

            if (__LOG_INFO__) AppLog.info(`✅ Proxy returned ${articles.length} articles, displaying ${limitedArticles.length} for ${stockSymbol}`);
            return limitedArticles;
        } catch (err) {
            if (__LOG_ERROR__) AppLog.error(`❌ Proxy news fetch failed for ${stockSymbol}: ${err.message}`);
            return [];
        }
    }

    async getWatchlistNews(userId, watchlistId) {
        try {
            if (!this.isInitialized) throw new Error('Proxy client not initialized.');

            // URL: /news/watchlist/:userID/:watchlistId
            const url = `${this.baseUrl}watchlist/${userId}/${watchlistId}`;
            if (__LOG_INFO__) AppLog.info(`📡 Proxy fetch watchlist news: ${url}`);

            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const json = await response.json();
            
             // Accept several shapes: [], {articles:[]}, {results:[]}, {data:[]}
            const rows = Array.isArray(json) ? json
                : Array.isArray(json.articles) ? json.articles
                    : Array.isArray(json.results) ? json.results
                        : Array.isArray(json.data) ? json.data
                            : [];

            // Normalize fields expected by UI with HTML entity decoding
            const articles = rows.map(a => ({
                title: he.decode(a.title || a.headline || 'No title available'),
                publisher: NewsProxyAPIManager.cleanPublisher(a.source, a.url || a.article_url, a.publisher || a.domain),
                publishedDate: a.published_at || a.publishedAt || a.date || a.published || new Date().toISOString(),
                url: a.url || a.article_url || '#',
                description: he.decode(a.description || a.summary || ''),
                tickers: Array.isArray(a.tickers) ? a.tickers : []
            }));

            const limitedArticles = articles.slice(0, 7);

            if (__LOG_INFO__) AppLog.info(`✅ Proxy returned ${articles.length} articles, displaying ${limitedArticles.length} for watchlist ${watchlistId}`);
            return limitedArticles;

        } catch (err) {
            if (__LOG_ERROR__) AppLog.error(`❌ Proxy watchlist news fetch failed for ${watchlistId}: ${err.message}`);
            return [];
        }
    }

    async fetchNarrativeTickerMap() {
        const url = 'https://api.palosverdestech.com/news-summary/latest-v3';
        try {
            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            console.log('[NewsProxyAPIManager] fetchNarrativeTickerMap raw data:', data);
            return data;
        } catch (err) {
            console.error(`[NewsProxyAPIManager] fetchNarrativeTickerMap failed: ${err.message}`);
            return null;
        }
    }

    static cleanPublisher(source, url, publisherField) {
        const rawSource = (source && source.name) ? source.name : (source || publisherField || '');
        let name = rawSource;
        
        const isGeneric = !name || 
            name.toLowerCase().includes('stocknewsapi') || 
            name.toLowerCase().includes('fmp_') || 
            name.toLowerCase().includes('mediastack') ||
            name.toLowerCase() === 'lulegacy';
            
        if (isGeneric && url) {
            try {
                const hostname = url.split('/')[2] || '';
                let host = hostname.toLowerCase();
                if (host.startsWith('www.')) host = host.slice(4);
                
                const domainMap = {
                    'marketwatch.com': 'MarketWatch',
                    'wsj.com': 'Wall Street Journal',
                    'seekingalpha.com': 'Seeking Alpha',
                    'bloomberg.com': 'Bloomberg',
                    'reuters.com': 'Reuters',
                    'cnbc.com': 'CNBC',
                    'globenewswire.com': 'GlobeNewswire',
                    'prnewswire.com': 'PR Newswire',
                    'businesswire.com': 'Business Wire',
                    'fool.com': 'The Motley Fool',
                    'investorplace.com': 'InvestorPlace',
                    'barrons.com': 'Barrons',
                    'thestreet.com': 'The Street',
                    'techcrunch.com': 'TechCrunch',
                    'foxbusiness.com': 'Fox Business',
                    'marketbeat.com': 'MarketBeat',
                    'investopedia.com': 'Investopedia',
                    'nytimes.com': 'New York Times',
                    'forbes.com': 'Forbes',
                    'businessinsider.com': 'Business Insider'
                };
                
                if (domainMap[host]) {
                    return domainMap[host];
                }
                
                const parts = host.split('.');
                if (parts.length >= 2) {
                    const domainName = parts[parts.length - 2];
                    return domainName.charAt(0).toUpperCase() + domainName.slice(1);
                }
                return host;
            } catch (e) {}
        }
        
        if (name === 'stocknewsapi_general') return 'General News';
        if (name.startsWith('fmp_')) return 'Financial News';
        if (name === 'mediastack_business') return 'Mediastack';
        
        return name || 'Unknown publisher';
    }

    static buildTickerPriceMap(data) {
        const map = new Map();
        (data?.tickers || []).forEach(t => {
            const prices = Array.isArray(t.symbol_prices) && t.symbol_prices.length
                ? t.symbol_prices
                : [{
                    symbol: t.symbol,
                    price: t.price ?? null,
                    change_pct: t.price_change_pct ?? null,
                    price_display: t.price_display ?? null,
                    price_direction: t.price_direction ?? null,
                }];

            prices.forEach(sp => {
                const sym = String(sp.symbol || '').toUpperCase().trim();
                if (!sym) return;
                if (map.has(sym)) return;
                map.set(sym, {
                    changePct:      sp.change_pct      ?? null,
                    price:          sp.price           ?? null,
                    priceDisplay:   sp.price_display   ?? null,
                    priceDirection: sp.price_direction ?? null,
                });
            });
        });
        return map;
    }
}

// Expose to window like your Mediastack manager does
window.NewsProxyAPIManager = NewsProxyAPIManager;
